import * as React from 'react';
import { FC, useState, useCallback, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Form } from 'reactstrap';
import { DocumentSettings } from '../../../../models';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import ButtonsContainer from '../../../../components/ButtonsContainer';
import { TextInputField, AutocompleteTextField } from '../../../../components/Forms/fields';
import { useGeneralSettings, useServicesSet } from '../../../../store/settings/hooks';
import { CheckInputField } from '../../../../components/Forms/fields/CheckInputField';
import { serviceId, serviceName } from '../../../../shared/appointmint/appointment';
import { Popover, Tooltip } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import PaletteIcon from '@material-ui/icons/Palette';
import styled from 'styled-components';
import {
    DOCUMENT_ICONS,
    COLOR_OPTIONS,
    getIconComponent,
    PickerButton,
    PopoverContent,
    PopoverTitle,
    IconGrid,
    IconOptionButton,
    ColorGrid,
    ColorSwatch,
    ClearButton,
} from '../../../../shared/documentIconPicker';
import AppButton from '../../../../components/AppButton';

const NameRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: var(--spacer-S);
`;

const NameFieldWrapper = styled.div`
    flex: 1;
`;

export interface DocumentConfigurationModalProps {
    defaultValue?: DocumentSettings,
    onClose?: () => void,
    onSave: (value: DocumentSettings) => void,
    onDelete: (id: string) => void,
}

const DocumentConfigurationModal : FC<DocumentConfigurationModalProps> = ({
    defaultValue,
    onClose,
    onSave,
    onDelete,
}) => {

    const t = useTranslations();

    const allAppointmentTypes = useServicesSet()

    const [document, setDocument] = useState<DocumentSettings>({
        id: '',
        name: '',
        availableForAppointmentsManager: false,
        availableForAllAppointmentTypes: false,
        availableForAppointmentTypes: [],
        ...defaultValue,
    });

    const settings = useGeneralSettings();

    const [iconAnchor, setIconAnchor] = useState<HTMLElement | null>(null);
    const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);

    useEffect(() => {
        defaultValue && setDocument(defaultValue)
    }, [defaultValue]);

    const updateName = useCallback((name: string) => setDocument(prev => ({...prev, name})), [setDocument]);

    const updateAppointmentTypes = useCallback((availableForAppointmentTypes: string[]) => {
        setDocument(prev => ({
            ...prev as DocumentSettings,
            availableForAppointmentTypes,
        }))
    }, [setDocument]);

    const updateAvailableForAppointmentsManager = useCallback((availableForAppointmentsManager: boolean) => {
        setDocument(prev => ({...prev as DocumentSettings, availableForAppointmentsManager}))
    }, [setDocument]);

    const updateAvailableForAllAppointmentTypes = useCallback((availableForAllAppointmentTypes: boolean) => {
        setDocument(prev => ({...prev as DocumentSettings, availableForAllAppointmentTypes, availableForAppointmentTypes: availableForAllAppointmentTypes ? [] : prev.availableForAppointmentTypes }))
    }, [setDocument]);

    const updateIcon = useCallback((icon: string | undefined) => {
        setDocument(prev => ({...prev as DocumentSettings, icon}));
        setIconAnchor(null);
    }, [setDocument]);

    const updateColor = useCallback((colorCode: string | undefined) => {
        setDocument(prev => ({...prev as DocumentSettings, colorCode}));
        setColorAnchor(null);
    }, [setDocument]);

    const CurrentIconComponent = getIconComponent(document.icon);
    const currentColor = document.colorCode;

    return (
        <Modal size="xl" isOpen={!!defaultValue}>
            <ModalHeader>{t(TK.documentType)} - {document?.name || t(TK.new)}</ModalHeader>
            <ModalBody>
                { document &&
                    <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
                        <NameRow>
                            <NameFieldWrapper>
                                <TextInputField
                                    id={'document-config-name'}
                                    label={t(TK.name)}
                                    value={document.name}
                                    onChange={updateName}
                                />
                            </NameFieldWrapper>

                            <Tooltip title={t(TK.selectColor)}>
                                <PickerButton
                                    id={'document-config-color-button'}
                                    $color={currentColor}
                                    $selected={!!colorAnchor}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => setColorAnchor(e.currentTarget)}
                                    size="small"
                                >
                                    <PaletteIcon style={{ color: currentColor || undefined }} />
                                </PickerButton>
                            </Tooltip>

                            <Tooltip title={t(TK.selectIcon)}>
                                <PickerButton
                                    id={'document-config-icon-button'}
                                    $color={currentColor}
                                    $selected={!!iconAnchor}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => setIconAnchor(e.currentTarget)}
                                    size="small"
                                >
                                    {CurrentIconComponent
                                        ? <CurrentIconComponent style={{ color: currentColor || undefined }} />
                                        : <DescriptionIcon style={{ color: currentColor || 'var(--appLigthGrey)' }} />
                                    }
                                </PickerButton>
                            </Tooltip>
                        </NameRow>

                        { !document.availableForAllAppointmentTypes &&
                            <AutocompleteTextField
                                id={'document-config-available-for'}
                                label={t(TK.availableFor)}
                                multiSelect
                                options={allAppointmentTypes.map(s => serviceName(s, settings))}
                                values={document.availableForAppointmentTypes.map(s => serviceName(s, settings))}
                                onValuesChanged={values => updateAppointmentTypes(values.map(v => serviceId(v, settings)))}
                            />
                        }
                        <CheckInputField
                            id={'document-config-available-for-all-services'}
                            label={t(TK.availableForAllServices)}
                            value={!!document.availableForAllAppointmentTypes}
                            onChange={updateAvailableForAllAppointmentTypes}
                        />
                        <CheckInputField
                            id={'document-config-available-for-appointments-manager'}
                            label={t(TK.availableForAdministratives)}
                            value={!!document.availableForAppointmentsManager}
                            onChange={updateAvailableForAppointmentsManager}
                        />

                    </Form>
                }

                <hr style={{width: '100%'}}/>

                <ButtonsContainer>
                    <AppButton id={'document-config-delete-button'} color="danger" onClick={() => onDelete(document.id)}>{t(TK.delete)}</AppButton>
                    <AppButton id={'document-config-save-button'} disabled={!document.name?.length} color="success" onClick={() => onSave(document)}>{t(TK.save)}</AppButton>
                    <AppButton id={'document-config-cancel-button'} onClick={onClose}>{t(TK.cancel)}</AppButton>
                </ButtonsContainer>

                <Popover
                    open={!!colorAnchor}
                    anchorEl={colorAnchor}
                    onClose={() => setColorAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <PopoverContent>
                        <PopoverTitle>{t(TK.selectColor)}</PopoverTitle>
                        <ColorGrid>
                            {COLOR_OPTIONS.map(color => (
                                <ColorSwatch
                                    key={color}
                                    $color={color}
                                    $selected={document.colorCode === color}
                                    onClick={() => updateColor(color)}
                                    title={color}
                                />
                            ))}
                        </ColorGrid>
                        {document.colorCode && (
                            <ClearButton onClick={() => updateColor(undefined)}>✕ clear</ClearButton>
                        )}
                    </PopoverContent>
                </Popover>

                <Popover
                    open={!!iconAnchor}
                    anchorEl={iconAnchor}
                    onClose={() => setIconAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <PopoverContent>
                        <PopoverTitle>{t(TK.selectIcon)}</PopoverTitle>
                        <IconGrid>
                            {DOCUMENT_ICONS.map(({ key, component: IconComp }) => (
                                <IconOptionButton
                                    key={key}
                                    $selected={document.icon === key}
                                    $color={currentColor}
                                    onClick={() => updateIcon(key)}
                                    size="small"
                                >
                                    <IconComp style={{ color: currentColor || undefined }} />
                                </IconOptionButton>
                            ))}
                        </IconGrid>
                        {document.icon && (
                            <ClearButton onClick={() => updateIcon(undefined)}>✕ clear</ClearButton>
                        )}
                    </PopoverContent>
                </Popover>
            </ModalBody>
        </Modal>
    );
}

export default DocumentConfigurationModal;
